/**
 * The little icon tile in front of a sync scope.
 *
 * Same shape as `BexioSyncStatusPill`: the icon map lives HERE, in a component,
 * not in `bexioSyncRunsView` — that module is documented as formatters and config
 * only, and putting React elements in it would make a pure helper file import the
 * icon library.
 *
 * DECORATION, NEVER THE NAME. Every caller renders the German scope label as real
 * text next to this tile, so the icon is `aria-hidden` and adds nothing to the
 * accessible name. An icon that had to be recognised to know which scope a row
 * describes would be worse than no icon.
 *
 * THE MAP HAS FOUR KEYS AND CANNOT GROW BY ACCIDENT. They are exactly the scopes
 * `bexio_sync_runs` can hold. There is no `payments` scope — payments are mirrored
 * INSIDE the invoices run — and the historic `payments` entry still sitting in the
 * shared `SCOPE_LABEL` map is not evidence of one. `bexioSyncRunsContract` pins
 * the key set against `SYNC_RUN_SCOPES` so the two cannot drift apart.
 *
 * ICON CHOICES, and why not the obvious ones:
 *   invoices        FileText  — already the Rechnungen icon on the overview KPI.
 *   contacts        Users     — already the Kontakte icon on the overview KPI.
 *   currency_repair BadgeCheck — a CHECK, not money and not a sync. This run
 *                   re-resolves a currency column on rows that were already
 *                   mirrored; it fetches nothing from Bexio. `CircleDollarSign`
 *                   would imply money moved and `RefreshCw` would imply data
 *                   arrived — both are claims this run does not support.
 *   init            Wrench    — a technical run that could not even build a
 *                   client. Tooling, not a data sync.
 */
import React from "react";
import { BadgeCheck, FileText, Users, Wrench } from "lucide-react";

/** Scope -> icon. Exported so the contract test can pin the key set. */
export const SYNC_SCOPE_ICON = {
  invoices: FileText,
  contacts: Users,
  currency_repair: BadgeCheck,
  init: Wrench,
};

export default function BexioSyncScopeIcon({ scope, className = "" }) {
  // An unknown scope still gets a tile so the row keeps its alignment — the
  // label beside it already falls back to the raw value rather than inventing a
  // name, and an empty gap would read as a rendering fault instead of as an
  // unfamiliar scope.
  const Icon = SYNC_SCOPE_ICON[scope] || Wrench;
  return (
    <span
      aria-hidden="true"
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground ${className}`}
    >
      <Icon className="h-4 w-4" />
    </span>
  );
}
